'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { Phone, User, CheckCircle2, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/providers/AuthProvider';
import { useBasePath } from '@/providers/BasePathProvider';
import { dateOfBirthValidation } from '@/lib/validations/auth';
import { formatPhoneNumber } from '@/lib/utils/format';


const nameRegex = /^[a-zA-ZÀ-ÿ\s'-]+$/;
const profileSchema = z.object({
  firstname: z.string().min(2, 'First name is required').max(50, 'First name must be at most 50 characters').regex(nameRegex, 'First name can only contain letters'),
  lastname: z.string().min(2, 'Last name is required').max(50, 'Last name must be at most 50 characters').regex(nameRegex, 'Last name can only contain letters'),
  phoneNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(
      (val) => !val || (val.length >= 10 && /^\+?[1-9][0-9\s-]{8,18}$/.test(val)),
      'Invalid phone number format. (e.g., +1 555 123-4567)'
    ),
  dateOfBirth: z.string().optional().or(z.literal('')).refine(
    (val) => {
      if (!val) return true;
      const [year, month, day] = val.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const now = new Date();
      if (isNaN(date.getTime())) return false;
      if (date.getFullYear() !== year || (date.getMonth() + 1) !== month || date.getDate() !== day) return false;
      if (date > now) return false;
      const maxAgeDate = new Date(); maxAgeDate.setFullYear(now.getFullYear() - 120);
      if (date < maxAgeDate) return false;
      const minAgeDate = new Date(); minAgeDate.setFullYear(now.getFullYear() - 18);
      if (date > minAgeDate) return false;
      return true;
    },
    (val) => {
      if (!val) return { message: '' };
      const [year, month, day] = val.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const now = new Date();
      if (isNaN(date.getTime()) || date.getFullYear() !== year || (date.getMonth() + 1) !== month || date.getDate() !== day) return { message: 'Please enter a physically valid date' };
      if (date > now) return { message: 'Date of birth cannot be in the future' };
      const maxAgeDate = new Date(); maxAgeDate.setFullYear(now.getFullYear() - 120);
      if (date < maxAgeDate) return { message: 'Please enter a realistic date of birth (max 120 years)' };
      return { message: 'You must be at least 18 years old' };
    }
  ),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export function EditProfileScreen() {
  const router = useRouter();
  const { user, updateUser } = useAuth();
  const { buildPath } = useBasePath();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null
  );

  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 18);
  const maxDateString = maxDate.toISOString().split('T')[0];

  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 120);
  const minDateString = minDate.toISOString().split('T')[0];

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    values: {
      firstname: user?.firstname || '',
      lastname: user?.lastname || '',
      phoneNumber: user?.phoneNumber || '',
      dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
    },
  });

  useEffect(() => {
    // Log validation errors if they occur
    if (Object.keys(errors).length > 0) {
      console.log('EditProfileScreen Form validation errors:', errors);
    }
  }, [errors]);

  useEffect(() => {
    if (!user) {
      const redirect = typeof window !== 'undefined' ? window.location.pathname : '/account/edit';
      router.push(buildPath(`/login?redirect=${encodeURIComponent(redirect)}`));
    }
  }, [user, router, buildPath]);

  if (!user) {
    return null;
  }

  const getErrorMessage = (error: any): string => {
    const msg = error.response?.data?.message;
    const raw = Array.isArray(msg) ? msg.join(' ') : String(msg ?? '');
    if (raw.includes('E11000') || raw.includes('duplicate key') || error.response?.status === 409) {
      return 'This email address is already registered to another account. Please use a different email.';
    }
    if (Array.isArray(msg)) return msg[0] || 'Failed to update profile';
    if (typeof msg === 'string') return msg;
    return 'Failed to update profile';
  };

  const onSubmit = async (data: ProfileFormData) => {
    setIsSubmitting(true);
    setStatus(null);
    console.log('EditProfileScreen submitting with data:', data);
    try {
      await updateUser({
        firstname: data.firstname,
        lastname: data.lastname,
        phoneNumber: data.phoneNumber ? data.phoneNumber.replace(/[\s-]/g, '') : undefined,
        dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : undefined,
      });
      setStatus({ type: 'success', message: 'Profile updated successfully' });
      router.push(buildPath('/account'));
    } catch (error: any) {
      setStatus({
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="container mx-auto px-4 pb-16 pt-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-primary-50"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-hprimary-50 text-hprimary-600">
              <User className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-500">
                Profile
              </p>
              <h1 className="text-2xl font-semibold text-slate-900">Edit core information</h1>
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-600">
            Update your name, email, and contact information so we can keep your deliveries and
            pharmacist support aligned with your preferences.
          </p>

          {status && (
            <div
              className={`mt-4 flex items-center gap-3 rounded-xl border-l-4 px-4 py-3 text-sm ${
                status.type === 'success'
                  ? 'border-l-green-500 bg-green-50 text-green-800'
                  : 'border-l-red-500 bg-red-50 text-red-700'
              }`}
            >
              {status.type === 'success'
                ? <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                : <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
              }
              <span>{status.message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="First name"
                placeholder="Taylor"
                {...register('firstname')}
                maxLength={50}
                error={errors.firstname?.message}
              />
              <Input
                label="Last name"
                placeholder="Reed"
                {...register('lastname')}
                maxLength={50}
                error={errors.lastname?.message}
              />
            </div>

            <div className="relative">
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    type="tel"
                    label="Phone number (optional)"
                    placeholder="+1 415 555 0100"
                    className="pl-10"
                    value={value}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      onChange(formatted);
                    }}
                    error={errors.phoneNumber?.message}
                  />
                )}
              />
              <Phone className="absolute left-3 top-[38px] h-4 w-4 text-slate-400 my-2" />
            </div>

            <div className="relative">
              <Input
                type="date"
                label="Date of Birth (optional)"
                className="pl-3"
                max={maxDateString}
                min={minDateString}
                {...register('dateOfBirth')}
                error={errors.dateOfBirth?.message}
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                type="submit" size="md" isLoading={isSubmitting}
                className="border-slate-300 text-slate-800 hover:opacity-95"
                variant="primary"
              >
                Save changes
              </Button>
              <Button
                type="button"
                variant="outline-solid"
                size="md"
                className="border-slate-300 text-slate-800 hover:bg-slate-50"
                onClick={() => router.push(buildPath('/account'))}
              >
                Cancel
              </Button>
            </div>
          </form>
        </motion.div>
      </div >
    </div >
  );
}
